import { defineMessages, useIntl } from 'react-intl';
import { LoadingContainer } from './LoadingContainer';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import Grid from '@mui/material/Grid';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import React from 'react';
import { Markdown } from './Markdown';
import { useFetchFileContents } from '../useFetchFileContents';

const messages = defineMessages({
  fetchRawTextErrorToast: {
    id: 'files.fileDetailsModal.text.fetchRawTextErrorToast',
    defaultMessage: 'An error occurred when fetching the text file',
  },
});

const useStyles = makeStyles(theme => ({
  root: {},
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  text: {
    whiteSpace: 'pre-wrap',
    fontSize: '0.8em',
  },
  content: {
    padding: theme.spacing(1),
  },
}));

const isMarkdownContentType = (file: any) =>
  ['application/json', 'application/xml', 'text/markdown'].includes(
    file.contentType,
  ) || ['.cwl', '.hl7'].filter(e => file.name.endsWith(e)).length;

export const RawText = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  file,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const [addNote] = useNotify();
  const useMarkdown = isMarkdownContentType(file);

  const { fileContents, isLoading, hasError } = useFetchFileContents(file);

  React.useEffect(() => {
    if (hasError) {
      addNote(formatMessage(messages.fetchRawTextErrorToast), 'error');
    }
  }, [addNote, formatMessage, hasError]);

  return (
    <React.Fragment>
      {isLoading && <LoadingContainer />}
      {!isLoading && fileContents && (
        <Grid item xs={12} className={classes.content}>
          {!useMarkdown && <pre className={classes.text}>{fileContents}</pre>}
          {file && file.contentType === 'application/json' && (
            <Markdown source={`\`\`\`json\n${fileContents}\n\`\`\``} />
          )}
          {file && file.contentType === 'application/xml' && (
            <Markdown source={`\`\`\`xml\n${fileContents}\n\`\`\``} />
          )}
          {file && file.name.endsWith('.cwl') && (
            <Markdown source={`\`\`\`yml\n${fileContents}\n\`\`\``} />
          )}
          {file && file.name.endsWith('.hl7') && (
            <Markdown source={`\`\`\`hl7\n${fileContents}\n\`\`\``} />
          )}
          {file && file.contentType === 'text/markdown' && (
            <Markdown source={fileContents} />
          )}
        </Grid>
      )}
    </React.Fragment>
  );
};
